import type {
  SubjectAssignmentForm,
  SubjectAssignmentStrategies,
  SubjectAssignmentStudent,
  SubjectAssignmentTask,
  SubjectAssignmentSnapshotSummary,
  SubjectAssignmentSnapshotDetail,
  SubjectAssignmentTaskUpdateRequest,
  WalkTeachingCoursePlan
} from '@/types'
import { http } from './request'

type BackendCombo = {
  combination: string
  count: number
  courseIds?: number[]
  courseNames?: string[]
}
type BackendAdminClass = {
  id: string
  name: string
  students?: number
  male?: number
  female?: number
  combos?: Array<{ combination: string; count: number; courseIds?: number[]; courseNames?: string[] }>
  isPure?: boolean
  primaryCombination?: string
  commonCount?: number
  modeType?: 'PRIORITY_THREE' | 'FIXED_TWO_WALK_ONE' | 'FIXED_ONE_WALK_TWO' | 'THREE_WALK'
  commonCourseIds?: number[]
  commonCourseNames?: string[]
}
type BackendTask = Omit<SubjectAssignmentTask, 'combinations' | 'adminClasses'> & {
  combinations?: BackendCombo[]
  adminClasses?: BackendAdminClass[]
  walkTeachingPlan?: WalkTeachingCoursePlan[]
}

function buildDisplayName(courseNames?: string[]): string {
  return courseNames?.length ? courseNames.join('-') : ''
}

function normalizeTask(task: BackendTask): SubjectAssignmentTask {
  const strategies = normalizeStrategies(task.strategies)
  const combinations = (task.combinations ?? []).map((item) => ({
    name: item.combination,
    fullName: buildDisplayName(item.courseNames) || item.combination,
    count: item.count,
    classCount: 0,
    allocatedCount: 0,
    unallocatedCount: item.count,
    courseIds: item.courseIds,
    courseNames: item.courseNames
  }))

  const adminClasses = (task.adminClasses ?? []).map((item) => ({
    id: item.id,
    name: item.name,
    students: item.students ?? 0,
    male: item.male ?? 0,
    female: item.female ?? 0,
    combos: (item.combos ?? []).map((c) => ({
      combination: c.combination,
      count: c.count,
      courseIds: c.courseIds,
      courseNames: c.courseNames
    })),
    isPure: item.isPure ?? false,
    primaryCombination: item.primaryCombination ?? '',
    commonCount: item.commonCount,
    modeType: item.modeType,
    commonCourseIds: item.commonCourseIds ?? [],
    commonCourseNames: item.commonCourseNames ?? []
  }))

  const allocatedByClass = new Map<string, number>()
  adminClasses.forEach((cls) => {
    cls.combos.forEach((combo) => {
      allocatedByClass.set(combo.combination, (allocatedByClass.get(combo.combination) ?? 0) + combo.count)
    })
  })
  const normalizedCombinations = combinations.map((combo) => {
    const allocatedCount = allocatedByClass.get(combo.name) ?? 0
    return {
      ...combo,
      allocatedCount,
      unallocatedCount: Math.max(0, combo.count - allocatedCount),
      classCount: adminClasses.filter((cls) => cls.combos.some((c) => c.combination === combo.name)).length
    }
  })

  return {
    ...task,
    strategies,
    totalClasses: task.totalClasses && task.totalClasses > 0 ? task.totalClasses : adminClasses.length,
    combinations: normalizedCombinations,
    adminClasses,
    walkTeachingPlan: task.walkTeachingPlan ?? []
  }
}

function normalizeStrategies(strategies?: SubjectAssignmentStrategies | null): SubjectAssignmentStrategies {
  return {
    priorityThree: strategies?.priorityThree ?? true,
    fixedTwoWalkOne: strategies?.fixedTwoWalkOne ?? false,
    fixedOneWalkTwo: strategies?.fixedOneWalkTwo ?? false,
    threeWalk: strategies?.threeWalk ?? false,
    crossSubject: strategies?.crossSubject ?? false,
    objectivePriority: strategies?.objectivePriority ?? [
      { key: 'modePriorityThree', rank: 1 },
      { key: 'modeFixedTwoWalkOne', rank: 2 },
      { key: 'modeFixedOneWalkTwo', rank: 3 },
      { key: 'genderBalance', rank: 4 }
    ],
    combinationPriorities: strategies?.combinationPriorities ?? [],
    coursePairPriorities: strategies?.coursePairPriorities ?? [],
    fixedOneCoursePriorities: strategies?.fixedOneCoursePriorities ?? [],
    teachingClassOptimization: {
      enabled: strategies?.teachingClassOptimization?.enabled ?? false,
      idealSize: strategies?.teachingClassOptimization?.idealSize ?? 40,
      minSize: strategies?.teachingClassOptimization?.minSize ?? 30,
      maxSize: strategies?.teachingClassOptimization?.maxSize ?? 45,
      hardBounds: strategies?.teachingClassOptimization?.hardBounds ?? false
    }
  }
}

export const subjectAssignmentApi = {
  async listTasks(): Promise<SubjectAssignmentTask[]> {
    const response = await http.get<BackendTask[]>('/subject-assignment/tasks')
    return response.data.map(normalizeTask)
  },

  async getTask(id: string): Promise<SubjectAssignmentTask | undefined> {
    const response = await http.get<BackendTask>(`/subject-assignment/tasks/${id}`)
    return normalizeTask(response.data)
  },

  async createTask(payload: SubjectAssignmentForm): Promise<SubjectAssignmentTask> {
    const sourceTaskId = payload.sourceTaskId ? Number(payload.sourceTaskId) : null
    if (sourceTaskId != null && Number.isNaN(sourceTaskId)) {
      throw new Error('关联选科任务ID无效')
    }
    const response = await http.post<BackendTask>('/subject-assignment/tasks', {
      name: payload.name,
      gradeName: payload.grade,
      sourceTaskId,
      remark: payload.remark,
      baseClassSize: payload.baseClassSize,
      classSizeVariance: payload.classSizeVariance,
      strategies: payload.strategies,
      courseMapping: payload.courseMapping,
      draftCurrentStep: payload.draftCurrentStep
    })
    return normalizeTask(response.data)
  },

  async updateTask(id: string, payload: SubjectAssignmentTaskUpdateRequest): Promise<SubjectAssignmentTask> {
    const rawSourceTaskId = payload.sourceTaskId
    const sourceTaskId = rawSourceTaskId == null || rawSourceTaskId === '' ? null : Number(rawSourceTaskId)
    if (sourceTaskId != null && Number.isNaN(sourceTaskId)) {
      throw new Error('关联选科任务ID无效')
    }
    const response = await http.put<BackendTask>(`/subject-assignment/tasks/${id}`, {
      name: payload.name,
      gradeName: payload.gradeName,
      remark: payload.remark,
      baseClassSize: payload.baseClassSize,
      classSizeVariance: payload.classSizeVariance,
      strategies: payload.strategies,
      courseMapping: payload.courseMapping,
      draftCurrentStep: payload.draftCurrentStep,
      sourceTaskId
    })
    return normalizeTask(response.data)
  },

  async removeTask(id: string): Promise<void> {
    await http.delete<void>(`/subject-assignment/tasks/${id}`)
  },

  async executeAssignment(id: string): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${id}/execute`, {})
    return normalizeTask(response.data)
  },

  async cancelQueued(id: string): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${id}/cancel-queued`, {})
    return normalizeTask(response.data)
  },

  async stopRun(id: string): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${id}/stop`, {})
    return normalizeTask(response.data)
  },

  async deleteAdminClass(taskId: string, classId: string, snapshotId?: number | null): Promise<void> {
    await http.post<void>(`/subject-assignment/tasks/${taskId}/admin-classes/delete`, {
      classId,
      snapshotId: snapshotId ?? undefined
    })
  },

  async renameAdminClass(taskId: string, classId: string, name: string, snapshotId?: number | null): Promise<SubjectAssignmentTask> {
    const response = await http.put<BackendTask>(`/subject-assignment/tasks/${taskId}/admin-classes/rename`, {
      classId,
      name,
      snapshotId: snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async createAdminClass(taskId: string, name: string, snapshotId?: number | null): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${taskId}/admin-classes/create`, {
      name,
      snapshotId: snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async reorderAdminClasses(
    taskId: string,
    classIds: string[],
    snapshotId?: number | null
  ): Promise<SubjectAssignmentTask> {
    const response = await http.put<BackendTask>(`/subject-assignment/tasks/${taskId}/admin-classes/reorder`, {
      classIds,
      snapshotId: snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async transferStudents(
    taskId: string,
    payload: { sourceClassId: string; targetClassId: string; combination: string; count: number; snapshotId?: number | null }
  ): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${taskId}/admin-classes/transfer`, {
      ...payload,
      snapshotId: payload.snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async assignUnassigned(
    taskId: string,
    payload: { targetClassId: string; combination: string; count: number; snapshotId?: number | null }
  ): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${taskId}/admin-classes/assign-unallocated`, {
      ...payload,
      snapshotId: payload.snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async unassignStudents(
    taskId: string,
    payload: { sourceClassId: string; combination: string; count: number; snapshotId?: number | null }
  ): Promise<SubjectAssignmentTask> {
    const response = await http.post<BackendTask>(`/subject-assignment/tasks/${taskId}/admin-classes/unassign`, {
      ...payload,
      snapshotId: payload.snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async updateWalkTeachingPlan(taskId: string, walkTeachingPlan: WalkTeachingCoursePlan[], snapshotId?: number | null): Promise<SubjectAssignmentTask> {
    const response = await http.put<BackendTask>(`/subject-assignment/tasks/${taskId}/walk-teaching-plan`, {
      walkTeachingPlan,
      snapshotId: snapshotId ?? undefined
    })
    return normalizeTask(response.data)
  },

  async getClassStudents(_taskId: string, _classId: string, _snapshotId?: number | null): Promise<SubjectAssignmentStudent[]> {
    const params: Record<string, string> = {}
    if (_snapshotId != null) {
      params.snapshotId = String(_snapshotId)
    }
    const response = await http.get<SubjectAssignmentStudent[]>(`/subject-assignment/tasks/${_taskId}/admin-classes/${_classId}/students`, params)
    return response.data
  },

  async listSourceTasks(gradeId?: number): Promise<Array<{ id: string; name: string; mode?: string }>> {
    const response = await http.get<Array<{ id: string; name: string; gradeName?: string; mode?: string }>>('/subject-assignment/source-tasks', gradeId ? { gradeId } : {})
    return response.data
  },

  async listSnapshots(taskId: string): Promise<SubjectAssignmentSnapshotSummary[]> {
    const response = await http.get<SubjectAssignmentSnapshotSummary[]>(`/subject-assignment/tasks/${taskId}/snapshots`)
    return response.data
  },

  async getSnapshotDetail(taskId: string, snapshotId: number): Promise<SubjectAssignmentSnapshotDetail> {
    const response = await http.get<SubjectAssignmentSnapshotDetail>(`/subject-assignment/tasks/${taskId}/snapshots/${snapshotId}/detail`)
    return response.data
  },

  async renameSnapshot(taskId: string, snapshotId: number, name: string): Promise<SubjectAssignmentSnapshotSummary> {
    const response = await http.put<SubjectAssignmentSnapshotSummary>(`/subject-assignment/tasks/${taskId}/snapshots/${snapshotId}/name`, { name })
    return response.data
  },

  async deleteSnapshot(taskId: string, snapshotId: number): Promise<void> {
    await http.delete<void>(`/subject-assignment/tasks/${taskId}/snapshots/${snapshotId}`)
  },

  async activateSnapshot(taskId: string, snapshotId: number): Promise<SubjectAssignmentSnapshotSummary> {
    const response = await http.post<SubjectAssignmentSnapshotSummary>(`/subject-assignment/tasks/${taskId}/snapshots/${snapshotId}/activate`, {})
    return response.data
  }
}
